import React, { useState, useCallback, useRef, useEffect, useContext } from 'react'

import { SymplecticIntegrator, IntegratorState } from "./Symplectic"

import { getAllVariables } from "tadiff"
import * as e from "tadiff/expressions"

import { AppContext, useAppReducer, AppState } from "./Context"
import EnergyTermList from './EnergyTermList'
import { ObjectList } from './ObjectList'
import { Tabs, TabPanel, TabList, Tab } from 'react-tabs'
import "react-tabs/style/react-tabs.css"
import { ObjectPropertyList } from './ObjectPropertyList'

const IntegratorStateCanvas = (props: { integratorState: IntegratorState }) => {
    const { state } = useContext(AppContext)!

    const canvasRef = useRef<HTMLCanvasElement>(null)

    const [scale, setScale] = useState(200 / state.initialPositionScale)
    const [offset, setOffset] = useState([0, 0])

    useEffect(() => {
        if (canvasRef.current) {
            const canvas = canvasRef.current
            const ctx = canvas.getContext("2d")
            if (ctx) {
                ctx.fillStyle = "rgb(20, 20, 20)"
                ctx.fillRect(0, 0, canvas.width, canvas.height)

                ctx.fillStyle = "red"
                for (const object of props.integratorState.objects) {
                    const [x, y] = [object.position[0], object.position[1]]

                    const [endX, endY] = [object.position[0] + object.momentum[0], object.position[1] + object.momentum[1]]
                    ctx.strokeStyle = "blue"
                    ctx.beginPath()
                    ctx.moveTo(scale * x + canvas.width / 2 + offset[0], scale * y + canvas.height / 2 + offset[1])
                    ctx.lineTo(scale * endX + canvas.width / 2 + offset[0], scale * endY + canvas.height / 2 + offset[1])
                    ctx.stroke()

                    ctx.fillRect(scale * x - 2 + canvas.width / 2 + offset[0], scale * y - 2 + canvas.height / 2 + offset[1], 4, 4)
                }

                ctx.strokeStyle = "lime"
                ctx.beginPath()
                ctx.moveTo(10, 10)
                ctx.lineTo(110, 10)
                ctx.stroke()
                ctx.fillStyle = "lime"
                ctx.fillText((100 / scale).toFixed(2), 50, 20)

                ctx.fillText(`t: ${props.integratorState.time.toFixed(2)}`, canvas.width - 50, 20)
            }
        }
    }, [props.integratorState, canvasRef, scale, offset])

    const onWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
        setScale(scale * (1 - 0.1 * Math.sign(e.deltaY)))
    }, [scale, setScale])

    const [scrolling, setScrolling] = useState(false)
    const [prevMousePos, setPrevMousePos] = useState([0, 0])
    const onMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (e.button === 0) {
            setScrolling(true)
            setPrevMousePos([e.clientX, e.clientY])
        }
    }, [setScrolling, setPrevMousePos])

    const onMouseUp = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (e.button === 0) {
            setScrolling(false)
        }
    }, [setScrolling])

    const onMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        if (scrolling) {
            const delta = [e.clientX - prevMousePos[0], e.clientY - prevMousePos[1]]

            setOffset([offset[0] + delta[0], offset[1] + delta[1]])
            setPrevMousePos([e.clientX, e.clientY])
        }
    }, [scrolling, prevMousePos, setPrevMousePos, offset])

    return <canvas width="1000" height="1000" ref={canvasRef} onWheel={onWheel}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} />
}

const integratorConstants = [
    { c: 0.2465881872786138277570711850755, d: 0.0833333333333333333333333333333 },
    { c: 0.6047073875057809013986983841500, d: 0.3977675859548440074217908138861 },
    { c: -0.4009869039788007481085648931513, d: -0.0393336931446257363828544842332 },
    { c: 0.0993826583888120379055906478517, d: 0.0582327738564483956277303370138 },
    { c: -0.4009869039788007481085648931513, d: 0.0582327738564483956277303370138 },
    { c: 0.6047073875057809013986983841500, d: -0.0393336931446257363828544842332 },
    { c: 0.2465881872786138277570711850755, d: 0.3977675859548440074217908138861 },
    { c: 0.0000000000000000000000000000000, d: 0.0833333333333333333333333333333 },
]

function makeSingleContext(context: e.EvaluationContext, position: number[], momentum: number[], dimensions: number, objectIndex: number, state: AppState) {
    const object = state.objects[objectIndex]
    for (const objectProperty of state.objectProperties) {
        if (object.propertyValues[objectProperty.name] !== undefined) {
            context.variableValues[objectProperty.name] = object.propertyValues[objectProperty.name]
        } else {
            context.variableValues[objectProperty.name] = objectProperty.defaultValue
        }
    }

    for (let dim = 0; dim < dimensions; dim++) {
        context.variableValues[`p${dim}`] = momentum[dim]
        context.variableValues[`q${dim}`] = position[dim]
    }
}

function makePairContext(context: e.EvaluationContext, position: number[], momentum: number[], otherPosition: number[], otherMomentum: number[], dimensions: number, objectIndex: number, otherObjectIndex: number, state: AppState) {
    const otherObject = state.objects[otherObjectIndex]
    for (const objectProperty of state.objectProperties) {
        if (otherObject.propertyValues[objectProperty.name] !== undefined) {
            context.variableValues[`${objectProperty.name}_b`] = otherObject.propertyValues[objectProperty.name]
        } else {
            context.variableValues[`${objectProperty.name}_b`] = objectProperty.defaultValue
        }
    }

    for (let dim = 0; dim < dimensions; dim++) {
        context.variableValues[`p${dim}_b`] = otherMomentum[dim]
        context.variableValues[`q${dim}_b`] = otherPosition[dim]
    }
}

const IntegratorComponent = () => {
    const { state, dispatch } = useContext(AppContext)!

    const [speed, setSpeed] = useState(1)
    const [stepSize, setStepSize] = useState(0.02)
    
    const { initialPositionScale, initialMomentumScale } = state

    const [integratorState, setIntegratorState] = useState<IntegratorState | null>(null)

    const [integrator, setIntegrator] = useState<SymplecticIntegrator | null>(null)

    const calcNextState = useCallback(() => {
        if (integrator && integratorState) {
            const newState = integrator.iterate(integratorState, stepSize)
            setIntegratorState(newState)
        }
    }, [integratorState, setIntegratorState, integrator, stepSize])

    useEffect(() => {
        const interval = setInterval(calcNextState, 1000 * stepSize / speed)
        return () => clearInterval(interval)
    }, [calcNextState, stepSize, speed])

    const simulate = useCallback(() => {
        const energyTerms = state.energyTerms

        const gradsPosition: e.Expression[][] = []
        const gradsMomentum: e.Expression[][] = []

        for (const energyTerm of energyTerms) {
            const termVariables = getAllVariables(energyTerm.expression)

            const energyTermGradsPosition = []
            const energyTermGradsMomentum = []

            for (let dim = 0; dim < state.dimensions; dim++) {
                const gradPosition = new e.Derivative(termVariables["q" + dim.toString()], energyTerm.expression)
                const gradMomentum = new e.Derivative(termVariables["p" + dim.toString()], energyTerm.expression)

                energyTermGradsPosition.push(gradPosition)
                energyTermGradsMomentum.push(gradMomentum)
            }

            gradsPosition.push(energyTermGradsPosition)
            gradsMomentum.push(energyTermGradsMomentum)
        }

        const getGradForObject = (objectIndex: number, position: number[], momentum: number[], integratorState: IntegratorState) => {
            const evaluationContext: e.EvaluationContext = {
                variableValues: {},
                noCache: true
            }

            const gradPosition: number[] = []
            const gradMomentum: number[] = []

            for (let dim = 0; dim < state.dimensions; dim++) {
                gradPosition.push(0)
                gradMomentum.push(0)
            }

            makeSingleContext(evaluationContext, position, momentum, state.dimensions, objectIndex, state)

            state.energyTerms.forEach((energyTerm, energyTermIndex) => {
                if (energyTerm.affectedObjects[objectIndex]) {
                    for (let dim = 0; dim < state.dimensions; dim++) {
                        gradPosition[dim] += gradsPosition[energyTermIndex][dim].evaluate(evaluationContext)
                        gradMomentum[dim] += gradsMomentum[energyTermIndex][dim].evaluate(evaluationContext)
                    }
                }
            })

            integratorState.objects.forEach((otherObject, otherObjectIndex) => {
                makePairContext(evaluationContext, position, momentum, otherObject.position, otherObject.momentum, state.dimensions, objectIndex, otherObjectIndex, state)

                state.energyTerms.forEach((energyTerm, energyTermIndex) => {
                    if (energyTerm.affectedObjectPairs[[objectIndex, otherObjectIndex].toString()]) {
                        for (let dim = 0; dim < state.dimensions; dim++) {
                            gradPosition[dim] += gradsPosition[energyTermIndex][dim].evaluate(evaluationContext)
                            gradMomentum[dim] += gradsMomentum[energyTermIndex][dim].evaluate(evaluationContext)
                        }
                    }
                })
            })

            return {
                gradPosition: gradPosition,
                gradMomentum: gradMomentum,
            }
        }

        const initialIntegratorState: IntegratorState = {
            time: 0,
            objects: []
        }

        state.objects.forEach((object, objectIndex) => {
            initialIntegratorState.objects.push({
                position: [Math.random() * initialPositionScale - initialPositionScale / 2, Math.random() * initialPositionScale - initialPositionScale / 2],
                momentum: [Math.random() * initialMomentumScale - initialMomentumScale / 2, Math.random() * initialMomentumScale - initialMomentumScale / 2],
            })
        })

        setIntegratorState(initialIntegratorState)
        setIntegrator(new SymplecticIntegrator(integratorConstants, getGradForObject))
    }, [state, initialPositionScale, initialMomentumScale])

    useEffect(simulate, [])

    return (
        <>
            <input type="button" value="Simulate" onClick={simulate} />

            <div>
                <label>Initial position scale: </label>
                <input type="text" onChange={e => {
                    const value = parseFloat(e.target.value)
                    if (!isNaN(value)) {
                        dispatch({
                            type: "setInitialScales",
                            positionScale: value,
                            momentumScale: initialMomentumScale
                        })
                    }
                }} defaultValue={initialPositionScale.toString()} />
            </div>
            <div>
                <label>Initial momentum scale: </label>
                <input type="text" onChange={e => {
                    const value = parseFloat(e.target.value)
                    if (!isNaN(value)) {
                        dispatch({
                            type: "setInitialScales",
                            positionScale: initialPositionScale,
                            momentumScale: value
                        })
                    }
                }} defaultValue={initialMomentumScale.toString()} />
            </div>
            <div>
                <label>Simulation speed: </label>
                <input type="range" min="0.1" max="10" value={speed} step="0.1" onChange={e => setSpeed(parseFloat(e.target.value))}></input>
                {speed}
            </div>
            <div>
                <label>Step size: </label>
                <input type="range" min="0.005" max="1" value={stepSize} step="0.005" onChange={e => setStepSize(parseFloat(e.target.value))}></input>
                {stepSize}
            </div>

            {integratorState && (<>
                <IntegratorStateCanvas integratorState={integratorState} />
            </>)}
        </>
    )
}

function Presets() {
    const { dispatch } = useContext(AppContext)!

    return (
        <div>
            <input type="button" value="Gravity" onClick={e => dispatch({ type: "loadPreset", preset: "gravity" })} />
            <input type="button" value="Coulomb" onClick={e => dispatch({ type: "loadPreset", preset: "coulomb" })} />
            <input type="button" value="Attractor" onClick={e => dispatch({ type: "loadPreset", preset: "attractor" })} />
            <input type="button" value="Nuclear" onClick={e => dispatch({ type: "loadPreset", preset: "nuclear" })} />
        </div>
    )
}

const App = () => {
    const [state, dispatch] = useAppReducer()

    return (
        <AppContext.Provider value={{ state: state, dispatch: dispatch }}>
            <Tabs>
                <TabList>
                    <Tab>Simulation</Tab>
                    <Tab>Objects</Tab>
                    <Tab>Energy Terms</Tab>
                    <Tab>Presets</Tab>
                </TabList>
                <TabPanel><IntegratorComponent /></TabPanel>
                <TabPanel><ObjectPropertyList /><ObjectList /></TabPanel>
                <TabPanel><EnergyTermList /></TabPanel>
                <TabPanel><Presets /></TabPanel>
            </Tabs>
        </AppContext.Provider >
    )
}

export default App